import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import React, { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import * as yup from "yup";
import Input from "../../../components/Input";
import { MUTATION_CHANGE_PASSWORD } from "../../../config/graphql/mutation";
import { QUERY_CURRENT_USER } from "../../../config/graphql/query";

interface ChangePasswordFieldValues {
  currentPassword: string;
  password: string;
  passwordConfirm: string;
}

/**
 * Changes the signed-in user's own password through `changePassword`, which
 * asks for the current one. Used on the Profile of roles that may not save the
 * full profile form (`updateUser`).
 */
const ChangePassword = React.memo(() => {
  const { t } = useTranslation(["profile", "resetPassword"]);

  const schema = useMemo(() => {
    return yup.object().shape({
      currentPassword: yup
        .string()
        .required(t("resetPassword:yup.password.required")),
      password: yup
        .string()
        .required(t("resetPassword:yup.password.required"))
        .min(8, t("resetPassword:yup.password.min", { count: 8 }))
        .matches(/[a-z]+/, t("resetPassword:yup.password.lowercase"))
        .matches(/[A-Z]+/, t("resetPassword:yup.password.uppercase"))
        .matches(/\d+/, t("resetPassword:yup.password.number"))
        .matches(/[@$!%*#?&]+/, t("resetPassword:yup.password.special")),
      passwordConfirm: yup
        .string()
        .oneOf(
          [yup.ref("password")],
          t("resetPassword:yup.confirmPassword.match"),
        ),
    });
  }, [t]);

  const methods = useForm<ChangePasswordFieldValues>({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    mode: "onChange",
    defaultValues: { currentPassword: "", password: "", passwordConfirm: "" },
  });

  const { data: userData } = useQuery<{ me: IUser }>(QUERY_CURRENT_USER);

  const hasPassword = useMemo(
    () => !!userData?.me?.authProviders?.includes("credentials"),
    [userData],
  );

  const [onChangePassword] = useMutation(MUTATION_CHANGE_PASSWORD);

  const history = useHistory();

  const onSubmit = useCallback(
    ({ currentPassword, password }: ChangePasswordFieldValues) => {
      return onChangePassword({
        variables: { input: { currentPassword, password } },
      })
        .then(() => {
          toast.success<string>(t("profile:changePassword.toast.success"));

          // Back to the role's start page ("/" redirects by role).
          history.push("/");
        })
        .catch((error: any) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [onChangePassword, history, t],
  );

  if (!userData?.me) {
    return null;
  }

  if (!hasPassword) {
    return <p>{t("profile:changePassword.noPassword")}</p>;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-lg-4 col-md-6 col-sm-12">
            <h5 className="mb-3">{t("profile:changePassword.title")}</h5>
            <div className="form-group">
              <label htmlFor="currentPassword">
                {t("profile:changePassword.form.currentPassword")}
              </label>
              <Input
                name="currentPassword"
                type="password"
                className="form-control"
                autoComplete="current-password"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">
                {t("profile:changePassword.form.password")}
              </label>
              <Input
                name="password"
                type="password"
                className="form-control"
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label htmlFor="passwordConfirm">
                {t("profile:changePassword.form.passwordConfirm")}
              </label>
              <Input
                name="passwordConfirm"
                type="password"
                className="form-control"
                autoComplete="new-password"
              />
            </div>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={methods.formState.isSubmitting}
            >
              {t("profile:changePassword.button.submit")}
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
});

export default ChangePassword;
